import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import api from '../api';

const icons = {
  dashboard: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>,
  bautagebuch: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><rect x="2" y="1" width="12" height="14" rx="1"/><line x1="5" y1="5" x2="11" y2="5"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="11" x2="8" y2="11"/></svg>,
  zeit: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6"/><polyline points="8,4 8,8 11,10"/></svg>,
  material: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><rect x="1" y="6" width="14" height="9" rx="1"/><path d="M5 6V4a3 3 0 0 1 6 0v2"/></svg>,
  fotos: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><rect x="1" y="4" width="14" height="10" rx="1"/><circle cx="8" cy="9" r="2.5"/><path d="M5 4l1.5-2h3L11 4"/></svg>,
  baustellen: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><polygon points="8,1 10,6 15,6 11,9.5 12.5,14.5 8,11.5 3.5,14.5 5,9.5 1,6 6,6"/></svg>,
  mitarbeiter: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><circle cx="6" cy="5" r="3"/><path d="M1 13c0-3 2-5 5-5s5 2 5 5"/><circle cx="12" cy="5" r="2"/><path d="M14 12c0-2-1-3.5-2-4"/></svg>,
  logout: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 16 16"><path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3"/><polyline points="11,11 14,8 11,5"/><line x1="14" y1="8" x2="6" y2="8"/></svg>,
  menu: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 18 18"><line x1="2" y1="5" x2="16" y2="5"/><line x1="2" y1="9" x2="16" y2="9"/><line x1="2" y1="13" x2="16" y2="13"/></svg>,
};

function initials(name) {
  return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stamped, setStamped] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stampTime, setStampTime] = useState('--:--');

  useEffect(() => {
    api.get('/zeiterfassung/status').then(r => {
      if (r.data.eingestempelt) { setStamped(true); setStampTime(r.data.entry.einstempel); }
    }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);

  const toggleStamp = async () => {
    try {
      if (!stamped) {
        const r = await api.post('/zeiterfassung/einstempeln', {});
        setStamped(true); setStampTime(r.data.einstempel);
      } else {
        await api.post('/zeiterfassung/ausstempeln');
        setStamped(false); setStampTime('--:--');
      }
    } catch (e) { alert(e.response?.data?.error || 'Fehler'); }
  };

  const titles = {
    '/': 'Übersicht',
    '/bautagebuch': 'Bautagebuch',
    '/zeiterfassung': 'Zeiterfassung',
    '/material': 'Materialverwaltung',
    '/fotos': 'Fotodokumentation',
    '/baustellen': 'Baustellen',
    '/mitarbeiter': 'Mitarbeiter',
  };

  return (
    <div className="app-layout">
      <div className={"sidebar-overlay" + (sidebarOpen ? " open" : "")} onClick={closeSidebar} />
      <aside className={"sidebar" + (sidebarOpen ? " open" : "")}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-text">DV ELEKTRO</div>
          </div>
          <div className="sidebar-subtitle">Mitarbeiterportal</div>
        </div>
        <div className="sidebar-user">
          <div className="avatar">{initials(user?.name)}</div>
          <div>
            <div className="user-name">{user?.name}</div>
            <div className="user-role">{user?.role === 'admin' ? 'Administrator' : 'Monteur'}</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">Hauptmenü</div>
          <NavLink to="/" end className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.dashboard} Übersicht</NavLink>
          <NavLink to="/bautagebuch" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.bautagebuch} Bautagebuch</NavLink>
          {user?.role === 'admin' && <NavLink to="/zeiterfassung" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.zeit} Zeiterfassung</NavLink>}
          <NavLink to="/material" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.material} Material</NavLink>
          <NavLink to="/fotos" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.fotos} Fotodokumentation</NavLink>
          <div className="nav-section">Verwaltung</div>
          <NavLink to="/baustellen" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.baustellen} Baustellen</NavLink>
          {user?.role === 'admin' && <NavLink to="/mitarbeiter" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')} onClick={closeSidebar}>{icons.mitarbeiter} Mitarbeiter</NavLink>}
          <div className="nav-link" onClick={handleLogout} style={{ cursor: 'pointer', marginTop: 4 }}>{icons.logout} Abmelden</div>
        </nav>
        <div className="sidebar-footer">
          <div className="checkin-card">
            <div className="checkin-label">{stamped ? 'Eingestempelt seit' : 'Nicht eingestempelt'}</div>
            <div className="checkin-time">{stampTime}</div>
            <button className={"btn-checkin " + (stamped ? 'aus' : 'ein')} onClick={toggleStamp}>
              {stamped ? 'Ausstempeln' : 'Einstempeln'}
            </button>
          </div>
        </div>
      </aside>
      <div className="main-area">
        <div className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="burger-btn" onClick={() => setSidebarOpen(s => !s)}>
              {icons.menu}
            </button>
            <span className="topbar-title">{titles[window.location.pathname] || 'DV Elektro Portal'}</span>
          </div>
          <div className="topbar-actions">
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
